import { yupResolver } from '@hookform/resolvers/yup';
import {
  Popover,
  PopoverTrigger,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from '@mindpal-co/mindpal-ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { z } from 'zod';

import Button from '@/components/Button';
import Checkbox from '@/components/Form/Fields/Checkbox';
import Icon from '@/components/Icon';
import Text from '@/components/Text';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { PopoverContent } from '@/components/ui/popover';
import { TooltipContent } from '@/components/ui/tooltip';
import { useExternalCandidatesCollections } from '@/hooks/useExternalCandidatesCollections';
import { useOptimisticUpdate } from '@/hooks/useOptimisticUpdate';
import { queries } from '@/queries';
import type { getExternalCandidatesCollections } from '@/services/externalCandidates';
import {
  addReferenceJobMatching,
  removeReferenceJobMatching,
} from '@/services/externalCandidates';

const JobMatchingReference = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const queryClient = useQueryClient();
  const { query } = useRouter();
  const collectionId = z.coerce.number().optional().parse(query.collection);

  const { setOptimisticData } = useOptimisticUpdate<
    Awaited<ReturnType<typeof getExternalCandidatesCollections>>
  >(queries.externalCandidates.collections.queryKey);

  const { data } = useExternalCandidatesCollections();
  const { data: jobMatchingOffersData } = useQuery(
    queries.jobMatching.list({
      pageIndex: 0,
      pageSize: 999,
    })
  );

  const handleOnSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: queries.externalCandidates.collections.queryKey,
    });
    setIsDialogOpen(false);
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: addReferenceJobMatching,
    mutationKey: ['addReferenceJobMatching'],
    onMutate: (queryData) =>
      setOptimisticData((oldData) => {
        if (!oldData) return oldData;
        const newReferenceJobMatching =
          jobMatchingOffersData?.results.filter(({ id }) =>
            queryData.jobMatchingId.includes(id)
          ) ?? [];
        const updatedResults = oldData.results.map((result) =>
          result.id === collectionId
            ? {
                ...result,
                referenceJobMatching: [
                  ...result.referenceJobMatching,
                  ...newReferenceJobMatching,
                ],
              }
            : result
        );
        return { ...oldData, results: updatedResults };
      }),
    onSuccess: handleOnSuccess,
  });

  const { mutate: removeReference } = useMutation({
    mutationFn: removeReferenceJobMatching,
    mutationKey: ['removeReferenceJobMatching'],
    onMutate: (queryData) =>
      setOptimisticData((oldData) => {
        if (!oldData) return oldData;

        const updatedResults = oldData.results.map((result) =>
          result.id === collectionId
            ? {
                ...result,
                referenceJobMatching: result.referenceJobMatching.filter(
                  ({ id }) => id !== queryData.jobMatchingId
                ),
              }
            : result
        );
        return { ...oldData, results: updatedResults };
      }),
    onSuccess: handleOnSuccess,
  });

  const { register, handleSubmit, watch } = useForm({
    defaultValues: { jobMatchingId: [] },
    resolver: yupResolver(schema),
  });

  const connectedMatchings = (data?.results ?? []).find(
    ({ id }) => id === collectionId
  )?.referenceJobMatching;

  const noConnectedOffersList = (jobMatchingOffersData?.results ?? []).filter(
    ({ id }) =>
      !connectedMatchings
        ?.map((connectedMatching) => connectedMatching.id)
        .includes(id)
  );

  if (!collectionId) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="gray"
          size="sm"
          className="h-fit px-6"
          prefixIcon="material-symbols:network-intel-node-rounded"
          suffixIcon="material-symbols:keyboard-arrow-down-rounded"
        >
          <span className="font-semibold text-primary-200">
            {connectedMatchings?.length}
          </span>{' '}
          Connected Job-Matchings
        </Button>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger>
              <Icon icon="material-symbols:info-outline" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <Text variant="body-small" className="text-neutral-1000">
                Connect a job-matching offer with a collection to automatically
                add every candidate in that collection. Any candidate added
                later is also synced to the offer and processed
              </Text>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </PopoverTrigger>
      <PopoverContent>
        <div className="flex flex-col">
          {connectedMatchings?.map(({ text, title, id }) => (
            <div
              key={id}
              className="flex flex-row justify-between border-b border-neutral-800 p-2 last:border-b-0"
            >
              <div className="flex flex-row gap-2">
                <Link
                  className="pt-1"
                  href={{
                    pathname: '/dashboard/job-matching/[id]',
                    query: { id: id.toString() },
                  }}
                  target="_blank"
                >
                  <Icon icon="material-symbols:open-in-new" fontSize={16} />
                </Link>
                <div className="flex flex-col">
                  <Text variant="body-small" className="font-semibold">
                    {title}
                  </Text>
                  <Text
                    variant="body-caption"
                    className="line-clamp-5 text-neutral-400"
                  >
                    {text}
                  </Text>
                </div>
              </div>
              <Button
                variant="unstyled"
                size="xs"
                className="h-fit py-1 text-error"
                onClick={() =>
                  removeReference({
                    collectionId,
                    jobMatchingId: id,
                  })
                }
                confirm="Are you sure you want to delete this connection?"
              >
                <Icon icon="material-symbols:delete-outline" fontSize={20} />
              </Button>
            </div>
          ))}
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full">Add new</Button>
          </DialogTrigger>
          <DialogContent>
            {!!noConnectedOffersList.length && (
              <form
                onSubmit={handleSubmit((formData) =>
                  mutate({
                    collectionId,
                    jobMatchingId: formData.jobMatchingId,
                  })
                )}
                className="flex flex-col gap-2 py-4"
              >
                <Button
                  type="submit"
                  isLoading={isLoading}
                  disabled={!watch('jobMatchingId').length}
                >
                  Connect
                </Button>
                {noConnectedOffersList.map(({ text, title, id }) => (
                  <div
                    key={id}
                    className="flex flex-row items-start justify-start gap-2 rounded-md border border-neutral-300 p-4 text-neutral-1000"
                  >
                    <Checkbox
                      {...register('jobMatchingId')}
                      value={id}
                      className="!py-1"
                    />
                    <div>
                      <Text variant="body-small" className="font-semibold">
                        {title}
                      </Text>
                      <Text variant="body-caption">{text}</Text>
                    </div>
                  </div>
                ))}
              </form>
            )}
            {!noConnectedOffersList.length && (
              <DialogTitle className="text-neutral-1000">
                All offers are already connected
              </DialogTitle>
            )}
          </DialogContent>
        </Dialog>
      </PopoverContent>
    </Popover>
  );
};

export default JobMatchingReference;

const schema = yup.object({
  jobMatchingId: yup.array().of(yup.number()),
});
